// Third-party
import React from 'react';
import moment from 'moment';
import debounce from 'lodash/debounce';

// Components
import { EditableInput } from '../../../components/inputs';
import { Pastille } from '../../../components/misc';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import Comparison from '@/types/comparison';
import { getComparisonTimeSinceSave } from '@/utils/comparison/details';

type HandleProps = {
  setComparisonName: (name: string) => void;
};

type StateProps = {
  comparison: Comparison;
  readOnly: boolean | undefined;
  isSaving: boolean;
};

type Props = HandleProps & StateProps;

const ComparisonTopBarName = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  const DEFAULT_COMPARISON_NAME = formatMessage('Comparison.DefaultName');

  const onComparisonNameChange = debounce((name: string) => {
    let trimmedName = name.trim();

    if (!trimmedName) {
      trimmedName = DEFAULT_COMPARISON_NAME;
    }

    props.setComparisonName(trimmedName);
  }, 250);

  const comparisonDate = props.comparison.lastRunAt ? moment(props.comparison.lastRunAt) : null;

  return (
    <div className="comparison-infos flex-row justify-content-center">
      <div className="comparison-name-container d-flex align-items-center justify-content-center">
        <EditableInput
          dark={!isLightVersion}
          data-private
          data-dd-privacy="mask"
          readOnly={props.readOnly}
          className="comparison-name"
          value={props.comparison.name || DEFAULT_COMPARISON_NAME}
          onValueChange={onComparisonNameChange}
          placeholder={formatMessage('Comparison.NamePlaceholder')}
        />
        {comparisonDate && props.readOnly && !props.isSaving && (
          <Pastille className="pastille-read-only">
            <FormattedMessage id="Common.ReadOnly" />
          </Pastille>
        )}
      </div>
      <div className="comparison-time-since-save-container d-flex align-items-center justify-content-center ">
        {comparisonDate && (
          <div className="comparison-pastilles d-flex">
            {props.readOnly && !props.isSaving && (
              <span className="comparison-is-historical-data">
                <FormattedMessage id="Comparison.HistoricalData" values={{ date: comparisonDate.format(formatMessage('Common.DateFormats.Short')) }} />
                {'\u00a0·\u00a0'}
              </span>
            )}
          </div>
        )}
        <span className="comparison-time-since-save">{getComparisonTimeSinceSave(props.comparison)}</span>
      </div>
    </div>
  );
};

export default ComparisonTopBarName;
